import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { LeadService } from '../../services/lead.service';
import {
  Lead, LeadStatus, LEAD_STATUS_LABELS, LEAD_SOURCE_LABELS,
} from '../../interfaces/crm/crm.interface';
import LeadForm from './LeadForm';
import LeadConvertDialog from './LeadConvertDialog';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useToast } from '../../context/ToastContext';

const STATUS_FILTERS: (LeadStatus | 'ALL')[] = [
  'ALL', 'NEW', 'CONTACTED', 'QUALIFIED', 'CONVERTED', 'DISQUALIFIED',
];

const statusChipColor = (status: LeadStatus): 'default' | 'info' | 'primary' | 'success' | 'error' => {
  switch (status) {
    case 'NEW': return 'info';
    case 'CONTACTED': return 'default';
    case 'QUALIFIED': return 'primary';
    case 'CONVERTED': return 'success';
    case 'DISQUALIFIED': return 'error';
    default: return 'default';
  }
};

const LeadList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.CRM_MANAGE);

  const [rows, setRows] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<LeadStatus | 'ALL'>('ALL');
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [convertLead, setConvertLead] = useState<Lead | null>(null);
  const [disqualifyLead, setDisqualifyLead] = useState<Lead | null>(null);
  const [disqualifyReason, setDisqualifyReason] = useState('');
  const [disqualifying, setDisqualifying] = useState(false);

  const load = useCallback(async () => {
    const institutionId = AuthService.getSelectedInstitution();
    try {
      setLoading(true);
      const res = await LeadService.listByInstitution(
        institutionId,
        statusFilter === 'ALL' ? undefined : statusFilter,
        page,
        size,
      );
      setRows(res.content);
      setTotal(res.totalElements);
    } catch (e) {
      toast.showApiError(e, 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [page, size, statusFilter, toast]);

  useEffect(() => { load(); }, [load]);

  const handleDisqualify = async () => {
    if (!disqualifyLead || disqualifyReason.trim() === '') return;
    setDisqualifying(true);
    try {
      await LeadService.disqualify(disqualifyLead.id, { reason: disqualifyReason.trim() });
      toast.showSuccess('Lead disqualified');
      setDisqualifyLead(null);
      setDisqualifyReason('');
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to disqualify lead');
    } finally {
      setDisqualifying(false);
    }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="crm.leadList.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Leads</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }} data-help-section="crm.leadList.filters">
          <TextField
            data-help="crm.leadList.status"
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value as LeadStatus | 'ALL'); setPage(0); }}
            sx={{ minWidth: 160 }}
          >
            {STATUS_FILTERS.map(s => (
              <MenuItem key={s} value={s}>
                {s === 'ALL' ? 'All' : LEAD_STATUS_LABELS[s]}
              </MenuItem>
            ))}
          </TextField>
          {canManage && (
            <Button data-help="crm.leadList.new" variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
              New Lead
            </Button>
          )}
        </Box>
      </Box>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} data-help-section="crm.leadList.table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-help="crm.leadList.col.name">Name</TableCell>
                <TableCell data-help="crm.leadList.col.company">Company</TableCell>
                <TableCell data-help="crm.leadList.col.email">Email</TableCell>
                <TableCell data-help="crm.leadList.col.phone">Phone</TableCell>
                <TableCell data-help="crm.leadList.col.source">Source</TableCell>
                <TableCell data-help="crm.leadList.col.status">Status</TableCell>
                <TableCell align="right" data-help="crm.leadList.col.actions">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => {
                const isConverted = r.status === 'CONVERTED';
                const isTerminal = isConverted || r.status === 'DISQUALIFIED';
                return (
                  <TableRow key={r.id} hover>
                    <TableCell
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/crm/leads/${r.id}`)}
                    >
                      {r.name}
                    </TableCell>
                    <TableCell>{r.companyName ?? '—'}</TableCell>
                    <TableCell>{r.email ?? '—'}</TableCell>
                    <TableCell>{r.phone ?? '—'}</TableCell>
                    <TableCell>{LEAD_SOURCE_LABELS[r.source]}</TableCell>
                    <TableCell>
                      <Chip size="small" color={statusChipColor(r.status)} label={LEAD_STATUS_LABELS[r.status]} />
                    </TableCell>
                    <TableCell align="right">
                      {canManage && !isTerminal && (
                        <Button data-help="crm.leadList.convert" size="small" onClick={() => setConvertLead(r)}>
                          Convert
                        </Button>
                      )}
                      {canManage && !isTerminal && (
                        <Button
                          data-help="crm.leadList.disqualify"
                          size="small"
                          color="error"
                          onClick={() => { setDisqualifyLead(r); setDisqualifyReason(''); }}
                        >
                          Disqualify
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }}
          />
        </TableContainer>
      )}

      <LeadForm
        open={formOpen}
        lead={null}
        onClose={() => setFormOpen(false)}
        onSaved={load}
      />

      {convertLead && (
        <LeadConvertDialog
          open={Boolean(convertLead)}
          lead={convertLead}
          onClose={() => setConvertLead(null)}
          onConverted={opp => { setConvertLead(null); navigate(`/crm/opportunities/${opp.id}`); }}
        />
      )}

      <Dialog open={Boolean(disqualifyLead)} onClose={() => !disqualifying && setDisqualifyLead(null)} fullWidth maxWidth="xs">
        <DialogTitle>Disqualify Lead</DialogTitle>
        <DialogContent>
          <TextField
            data-help="crm.leadList.dlg.reason"
            autoFocus
            fullWidth
            label="Reason"
            value={disqualifyReason}
            onChange={e => setDisqualifyReason(e.target.value)}
            multiline
            minRows={2}
            sx={{ mt: 1 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button data-help="crm.leadList.dlg.cancel" onClick={() => setDisqualifyLead(null)} disabled={disqualifying}>Cancel</Button>
          <Button
            data-help="crm.leadList.dlg.confirm"
            variant="contained"
            color="error"
            onClick={handleDisqualify}
            disabled={disqualifying || disqualifyReason.trim() === ''}
          >
            {disqualifying ? <CircularProgress size={20} /> : 'Disqualify'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadList;
